import type {ReactNode} from "react";
import styled from "styled-components";
import type {IError} from "../../../data/TransportLayer";
import type {
	ValidateBoundaryDetailsValidateObjectDto,
	ValidatePortfolioDetailsValidateObjectDto,
	ValidateSpaceDetailsValidateObjectDto,
	ValidateXyiconDetailsValidateObjectDto,
} from "../../../generated/api/base";
import {AccessStatus} from "../../../generated/api/base";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import type {AppState} from "../../../data/state/AppState";
import {NavigationEnum} from "../../../Enums";
import {colorPalette} from "../styles/colorPalette";
import OpenPortfolioIcon from "../icons/arrow-up-right-from-square.svg?react";

type ModuleName = "Xyicon" | "Portfolio" | "Boundary" | "Space";

export enum ReasonForNotAvailable {
	Loading = 0,
	DifferentPortfolio = 1,
	ItemRefIdNotValid = 2,
	ItemDeleted = 3,
	NoPermission = 4,
}

export interface INotAvailableInfo {
	reason: ReasonForNotAvailable;
	title: string;
	details: ReactNode;
}

export const getLoadingInfo = (): INotAvailableInfo => ({
	reason: ReasonForNotAvailable.Loading,
	title: "Loading...",
	details: "Please wait...",
});

const getNoPermissionInfo = (moduleName: ModuleName): INotAvailableInfo => ({
	reason: ReasonForNotAvailable.NoPermission,
	title: `${moduleName} Access Restricted`,
	details: `You do not have permission to the ${moduleName} you are trying to access. Please contact your administrator if you need access.`,
});

export const getGeneralValidationErrorTitle = (moduleName: ModuleName) => `${StringUtils.capitalize(moduleName)} Not Available`;
export const getGeneralValidationErrorMessage = (moduleName: ModuleName) =>
	`The URL is invalid or the ${moduleName} can't be accessed. Reach out to your administrator.`;

export const processValidationResult = (
	moduleName: ModuleName,
	appState: AppState,
	result?:
		| ValidateBoundaryDetailsValidateObjectDto
		| ValidateXyiconDetailsValidateObjectDto
		| ValidatePortfolioDetailsValidateObjectDto
		| ValidateSpaceDetailsValidateObjectDto,
	error?: IError,
): INotAvailableInfo => {
	if (result) {
		const {activePortfolio, actions} = appState;
		const navigation = appState.app.navigation;

		const itemRefId =
			(result as ValidateBoundaryDetailsValidateObjectDto).details.boundaryRefID ??
			(result as ValidateXyiconDetailsValidateObjectDto).details.xyiconRefID ??
			(result as ValidatePortfolioDetailsValidateObjectDto).details.portfolioRefID;

		if (result.status === AccessStatus.NoPermissions) {
			return getNoPermissionInfo(moduleName);
		} else if (result.status === AccessStatus.Deleted) {
			return {
				reason: ReasonForNotAvailable.ItemDeleted,
				title: `${moduleName} is Deleted`,
				details: `You don't have access to the ${moduleName} because it has been deleted. Please contact your administrator for more help.`,
			};
		} else if (result.details.portfolioID && result.details.portfolioID !== activePortfolio?.id) {
			const relevantPortfolio = actions.getPortfolioById(result.details.portfolioID);
			if (!relevantPortfolio) {
				return getNoPermissionInfo(moduleName);
			} else if (["Xyicon", "Boundary"].includes(moduleName)) {
				const onPortfolioNameClick = () => {
					navigation.goApp({
						mainNav: NavigationEnum.DETAILS,
						portfolioRefId: relevantPortfolio.refId,
						subNavs: [`${moduleName.toLowerCase()}/${itemRefId}`],
					});
				};

				return {
					reason: ReasonForNotAvailable.DifferentPortfolio,
					title: "",
					details: (
						<div>
							The item you're trying to view is in{" "}
							<PortfolioButtonStyled onClick={onPortfolioNameClick}>
								{(result as ValidateBoundaryDetailsValidateObjectDto | ValidateXyiconDetailsValidateObjectDto).details.portfolioName}
								<OpenPortfolioIcon
									width="16"
									height="16"
								/>
							</PortfolioButtonStyled>
							. Switch to that portfolio or close this view.
						</div>
					),
				};
			} else if (moduleName === "Space") {
				return {
					reason: ReasonForNotAvailable.DifferentPortfolio,
					title: "Switch Portfolio to View This Space",
					details: `The item you're trying to view is in ${relevantPortfolio?.name}. Switch the Portfolio to access the Space`,
				};
			}
		} else {
			// No reason why we shouldn't have access, so probably loading is still in progress
			return getLoadingInfo();
		}
	} else if (error) {
		return {
			reason: ReasonForNotAvailable.ItemRefIdNotValid,
			title: getGeneralValidationErrorTitle(moduleName),
			details: getGeneralValidationErrorMessage(moduleName),
		};
	} else {
		return getLoadingInfo();
	}
};

const PortfolioButtonStyled = styled.div`
	display: inline;
	color: ${colorPalette.primary.c500Primary};
	text-decoration: underline;
	cursor: pointer;
`;
